<template>
  <div class="wrong-book bg-neutral-50 min-h-screen">
    <main class="container mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <!-- Hero -->
      <section class="text-center mb-8">
        <div
          class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-50 text-rose-500 text-xs font-medium mb-4"
        >
          <i class="fa fa-bookmark"></i>
          错一道，会一类
        </div>
        <h1 class="text-3xl md:text-5xl font-bold text-neutral-900 mb-4">错题本</h1>
        <p class="text-base md:text-lg text-neutral-500 max-w-2xl mx-auto">
          把最核心的错题放进来，AI 帮你分析错因、串联知识点，并生成同类题练到会为止。
        </p>
      </section>

      <!-- 工具栏 -->
      <section class="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div class="flex gap-2 overflow-x-auto pb-1">
          <button
            v-for="s in subjectTabs"
            :key="s"
            @click="switchSubject(s)"
            class="px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors"
            :class="
              subject === s
                ? 'bg-primary text-white font-medium'
                : 'bg-white text-neutral-600 border border-neutral-200 hover:border-primary hover:text-primary'
            "
          >
            {{ s }}
          </button>
        </div>
        <button
          @click="openCreate"
          class="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90"
        >
          <i class="fa fa-plus mr-1"></i> 收录错题
        </button>
      </section>

      <!-- 未配置 AI 提示 -->
      <div
        v-if="!aiReady"
        class="mb-5 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-700"
      >
        <i class="fa fa-exclamation-circle mr-1"></i>
        AI 分析服务暂不可用，错题仍可正常收录。
      </div>

      <!-- 加载 -->
      <div v-if="loading" class="text-center py-16 text-neutral-400">
        <i class="fa fa-circle-o-notch fa-spin text-2xl"></i>
      </div>

      <!-- 空态 -->
      <div
        v-else-if="!list.length"
        class="bg-white rounded-xl border border-dashed border-neutral-300 py-16 text-center"
      >
        <i class="fa fa-book text-3xl text-neutral-300 mb-3"></i>
        <p class="text-neutral-500 text-sm">还没有收录错题</p>
        <button
          @click="openCreate"
          class="mt-4 px-4 py-2 rounded-lg bg-primary text-white text-sm"
        >
          收录第一道
        </button>
      </div>

      <!-- 错题列表 -->
      <div v-else class="space-y-4">
        <div
          v-for="b in list"
          :key="b.id"
          class="bg-white rounded-xl border border-neutral-200 overflow-hidden"
        >
          <!-- 头部 -->
          <div class="p-4 md:p-5">
            <div class="flex items-start gap-3">
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap mb-2">
                  <span
                    v-if="b.subject"
                    class="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary"
                    >{{ b.subject }}</span
                  >
                  <span v-if="b.grade" class="text-[11px] text-neutral-400">{{ b.grade }}</span>
                  <span
                    v-if="b.analyzed"
                    class="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600"
                  >
                    <i class="fa fa-check mr-1"></i>已分析
                  </span>
                  <span v-else class="text-[11px] text-neutral-400">未分析</span>
                </div>
                <p class="text-sm text-neutral-800 whitespace-pre-wrap leading-relaxed">
                  {{ b.content || "（仅图片）" }}
                </p>
                <div v-if="b.knowledgePoints" class="mt-2 flex flex-wrap gap-1.5">
                  <span
                    v-for="(k, i) in b.knowledgePoints.split(',')"
                    :key="i"
                    class="text-[11px] px-2 py-0.5 rounded bg-neutral-100 text-neutral-600"
                    >{{ k }}</span
                  >
                </div>
              </div>
              <img
                v-if="b.imageUrl"
                :src="fullUrl(b.imageUrl)"
                class="w-20 h-20 object-cover rounded-lg border border-neutral-100 cursor-pointer flex-shrink-0"
                @click="preview(b.imageUrl)"
                alt=""
              />
            </div>

            <div class="flex items-center gap-2 mt-3 pt-3 border-t border-neutral-100">
              <button
                @click="toggleExpand(b.id)"
                class="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-600 hover:border-primary hover:text-primary"
              >
                {{ expandedId === b.id ? "收起" : b.analyzed ? "查看分析" : "查看" }}
              </button>
              <button
                @click="analyze(b)"
                :disabled="analyzingId === b.id"
                class="px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-medium disabled:opacity-50"
              >
                <i
                  :class="analyzingId === b.id ? 'fa fa-circle-o-notch fa-spin' : 'fa fa-magic'"
                  class="mr-1"
                ></i>
                {{ analyzingId === b.id ? "分析中" : b.analyzed ? "重新分析" : "AI 分析" }}
              </button>
              <button
                @click="openEdit(b)"
                class="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-600 hover:border-primary hover:text-primary"
              >
                <i class="fa fa-pencil"></i>
              </button>
              <button
                @click="remove(b)"
                class="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-400 hover:border-red-300 hover:text-red-500"
              >
                <i class="fa fa-trash-o"></i>
              </button>
            </div>
          </div>

          <!-- 展开：分析结果 -->
          <div v-if="expandedId === b.id" class="border-t border-neutral-100 bg-neutral-50 p-4 md:p-5">
            <div v-if="!b.analyzed" class="text-sm text-neutral-500">
              还没有分析结果，点上面的「AI 分析」试试。
            </div>
            <div v-else class="space-y-5">
              <!-- 我的答案 / 正确答案 -->
              <div v-if="b.myAnswer || b.correctAnswer" class="grid sm:grid-cols-2 gap-3">
                <div v-if="b.myAnswer" class="bg-white rounded-lg p-3 border border-neutral-200">
                  <p class="text-[11px] text-neutral-400 mb-1">学生的答案</p>
                  <p class="text-sm text-red-500">{{ b.myAnswer }}</p>
                </div>
                <div v-if="b.correctAnswer" class="bg-white rounded-lg p-3 border border-neutral-200">
                  <p class="text-[11px] text-neutral-400 mb-1">正确答案</p>
                  <p class="text-sm text-emerald-600">{{ b.correctAnswer }}</p>
                </div>
              </div>

              <!-- 错因分析 -->
              <div>
                <h4 class="text-sm font-bold text-neutral-900 mb-2">
                  <i class="fa fa-search text-primary mr-1"></i>错因分析
                </h4>
                <p class="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap">
                  {{ b.analysis }}
                </p>
              </div>

              <!-- 知识点联想 -->
              <div v-if="b.relatedPoints && b.relatedPoints.length">
                <h4 class="text-sm font-bold text-neutral-900 mb-2">
                  <i class="fa fa-sitemap text-sky-500 mr-1"></i>知识点联想
                </h4>
                <div class="space-y-2">
                  <div
                    v-for="(r, i) in b.relatedPoints"
                    :key="i"
                    class="bg-white rounded-lg p-3 border border-neutral-200"
                  >
                    <p class="text-sm font-medium text-neutral-800">{{ r.point }}</p>
                    <p class="text-xs text-neutral-500 mt-0.5">{{ r.why }}</p>
                  </div>
                </div>
              </div>

              <!-- 举一反三 -->
              <div v-if="b.variants && b.variants.length">
                <h4 class="text-sm font-bold text-neutral-900 mb-2">
                  <i class="fa fa-lightbulb-o text-amber-500 mr-1"></i>举一反三（{{ b.variants.length }} 道）
                </h4>
                <div class="space-y-2">
                  <div
                    v-for="(v, i) in b.variants"
                    :key="i"
                    class="bg-white rounded-lg p-3 border border-neutral-200"
                  >
                    <div class="flex items-start gap-2">
                      <span
                        class="text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 flex-shrink-0"
                        >{{ v.level || "变式" }}</span
                      >
                      <p class="text-sm text-neutral-800 whitespace-pre-wrap flex-1">
                        {{ v.question }}
                      </p>
                    </div>
                    <button
                      class="mt-2 text-xs text-primary hover:underline"
                      @click="showAnswer[i] = !showAnswer[i]"
                    >
                      {{ showAnswer[i] ? "收起答案" : "查看答案" }}
                    </button>
                    <p
                      v-if="showAnswer[i]"
                      class="mt-2 text-sm text-emerald-700 bg-emerald-50 rounded p-2 whitespace-pre-wrap"
                    >
                      {{ v.answer }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 新增 / 编辑 -->
    <div
      v-if="editVisible"
      class="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8 px-4"
      @click.self="editVisible = false"
    >
      <div class="bg-white rounded-2xl w-full max-w-2xl p-6 shadow-xl">
        <div class="flex items-center justify-between mb-5">
          <h2 class="text-lg font-bold text-neutral-900">
            {{ form.id ? "编辑错题" : "收录错题" }}
          </h2>
          <button class="text-neutral-400 hover:text-neutral-600" @click="editVisible = false">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">学科</label>
              <select
                v-model="form.subject"
                class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white focus:outline-none focus:border-primary"
              >
                <option v-for="s in subjects" :key="s" :value="s">{{ s }}</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">年级</label>
              <input
                v-model="form.grade"
                placeholder="如：三年级"
                class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <!-- 图片识别 -->
          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">
              题目图片（可选，自动识别成文字）
            </label>
            <div class="flex items-center gap-3">
              <label
                class="px-3 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-600 cursor-pointer hover:border-primary hover:text-primary"
              >
                <input
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="pickImage"
                />
                <i class="fa fa-camera mr-1"></i>
                {{ ocrLoading ? "识别中..." : "拍照 / 选图" }}
              </label>
              <img
                v-if="form.imageUrl"
                :src="fullUrl(form.imageUrl)"
                class="h-12 rounded border border-neutral-200"
                alt=""
              />
            </div>
          </div>

          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">题目内容 *</label>
            <textarea
              v-model="form.content"
              rows="4"
              placeholder="可以直接粘贴题目，或上传图片自动识别"
              class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary resize-none"
            ></textarea>
          </div>

          <div class="grid sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">学生的答案</label>
              <textarea
                v-model="form.myAnswer"
                rows="2"
                placeholder="当时写的是什么"
                class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary resize-none"
              ></textarea>
            </div>
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">正确答案</label>
              <textarea
                v-model="form.correctAnswer"
                rows="2"
                placeholder="正确答案（可选）"
                class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary resize-none"
              ></textarea>
            </div>
          </div>

          <label class="flex items-center gap-2 text-sm text-neutral-700">
            <input v-model="analyzeAfterSave" type="checkbox" />
            保存后立即做 AI 分析
          </label>
        </div>

        <div class="flex justify-end gap-2 mt-6">
          <button
            class="px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-600"
            @click="editVisible = false"
          >
            取消
          </button>
          <button
            class="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-50"
            :disabled="saving"
            @click="save"
          >
            {{ saving ? "保存中..." : "保存" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";
import { requireLoginPage, isLogin } from "@/utils/authGuard";

export default {
  name: "WrongBookPage",
  data() {
    return {
      loading: false,
      saving: false,
      ocrLoading: false,
      analyzingId: "",
      aiReady: true,

      subject: "全部",
      subjectTabs: ["全部", "数学", "语文", "英语", "物理", "化学", "生物", "科学"],
      subjects: [
        "语文",
        "数学",
        "英语",
        "物理",
        "化学",
        "生物",
        "政治",
        "历史",
        "地理",
        "科学",
        "道德与法治",
        "信息技术",
      ],

      list: [],
      expandedId: "",
      showAnswer: {},

      editVisible: false,
      analyzeAfterSave: false,
      form: this.emptyForm(),
    };
  },
  mounted() {
    if (!requireLoginPage(this, "错题本")) return;
    this.load();
  },
  methods: {
    emptyForm() {
      return {
        id: "",
        subject: "数学",
        grade: "",
        content: "",
        imageUrl: "",
        myAnswer: "",
        correctAnswer: "",
      };
    },

    fullUrl(p) {
      if (!p) return "";
      if (p.startsWith("http")) return p;
      return p.startsWith("/") ? p : "/" + p;
    },

    async load() {
      if (!isLogin()) return;
      this.loading = true;
      try {
        const res = await this.$axios({
          url: "wrongbook/list",
          method: "GET",
          params: { subject: this.subject === "全部" ? "" : this.subject },
        });
        if (res.code === 200 && res.data) {
          this.list = res.data.list || [];
          this.aiReady = res.data.aiReady !== false;
        } else {
          ElMessage.error(res.msg || "加载失败");
        }
      } catch (e) {
        ElMessage.error("加载失败，请稍后重试");
      }
      this.loading = false;
    },

    switchSubject(s) {
      this.subject = s;
      this.load();
    },

    toggleExpand(id) {
      this.expandedId = this.expandedId === id ? "" : id;
      if (this.expandedId) this.showAnswer = {};
    },

    preview(url) {
      window.open(this.fullUrl(url), "_blank");
    },

    openCreate() {
      if (!requireLoginPage(this, "收录错题")) return;
      this.form = this.emptyForm();
      this.analyzeAfterSave = true;
      this.editVisible = true;
    },

    openEdit(b) {
      this.form = {
        id: b.id,
        subject: b.subject || "数学",
        grade: b.grade || "",
        content: b.content || "",
        imageUrl: b.imageUrl || "",
        myAnswer: b.myAnswer || "",
        correctAnswer: b.correctAnswer || "",
      };
      this.analyzeAfterSave = false;
      this.editVisible = true;
    },

    /** 选图：同时做 OCR 与图片上传 */
    async pickImage(ev) {
      const file = ev.target.files && ev.target.files[0];
      if (!file) return;
      if (file.size > 8 * 1024 * 1024) {
        ElMessage.warning("图片请控制在 8MB 以内");
        ev.target.value = "";
        return;
      }
      this.ocrLoading = true;

      // 1. 上传保存图片
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("UploadType", "MutipleImage");
        const d = new Date();
        const ymd =
          d.getFullYear() +
          "/" +
          String(d.getMonth() + 1).padStart(2, "0") +
          "/" +
          String(d.getDate()).padStart(2, "0");
        fd.append("businessType", "wrongbook/" + ymd);
        const up = await this.$axios({
          url: "upload",
          method: "POST",
          data: fd,
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (up.code === 200 && up.data && up.data.uploads) {
          this.form.imageUrl = up.data.uploads.files[0].o.fullPath;
        }
      } catch (e) {
        /* 图片保存失败不阻断识别 */
      }

      // 2. 识别成文字
      try {
        const fd2 = new FormData();
        fd2.append("file", file);
        const res = await this.$axios({
          url: "wrongbook/ocr",
          method: "POST",
          data: fd2,
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120 * 1000,
        });
        if (res.code === 200 && res.data && res.data.text) {
          this.form.content = res.data.text;
          ElMessage.success("已识别，请核对题目文字");
        } else {
          ElMessage.warning(res.msg || "没有识别到文字，请手动输入");
        }
      } catch (e) {
        ElMessage.warning("图片识别失败，请手动输入题目");
      }

      this.ocrLoading = false;
      ev.target.value = "";
    },

    async save() {
      if (!this.form.content.trim() && !this.form.imageUrl) {
        ElMessage.warning("请填写题目内容或上传图片");
        return;
      }
      this.saving = true;
      try {
        const res = await this.$axios({
          url: "wrongbook/save",
          method: "POST",
          data: { data: await this.$aesEncrypt(this.form) },
        });
        if (res.code === 200) {
          ElMessage.success("已保存");
          this.editVisible = false;
          await this.load();
          if (this.analyzeAfterSave && res.data && res.data.id) {
            const saved = this.list.find((x) => x.id === res.data.id) || res.data;
            await this.analyze(saved);
          }
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      } catch (e) {
        ElMessage.error("保存失败，请稍后重试");
      }
      this.saving = false;
    },

    async analyze(b) {
      if (!b || !b.id) return;
      this.analyzingId = b.id;
      try {
        const res = await this.$axios({
          url: "wrongbook/analyze",
          method: "POST",
          data: { data: await this.$aesEncrypt({ id: b.id }) },
          timeout: 180 * 1000,
        });
        if (res.code === 200 && res.data) {
          const idx = this.list.findIndex((x) => x.id === b.id);
          if (idx >= 0) this.list.splice(idx, 1, res.data);
          this.expandedId = b.id;
          this.showAnswer = {};
          ElMessage.success("分析完成");
        } else {
          ElMessage.error(res.msg || "分析失败");
        }
      } catch (e) {
        ElMessage.error(
          "分析失败：" + ((e && e.message) || "请稍后重试")
        );
      }
      this.analyzingId = "";
    },

    async remove(b) {
      try {
        await ElMessageBox.confirm(`确定删除这道${b.subject || ""}错题？`, "删除错题", {
          confirmButtonText: "删除",
          cancelButtonText: "取消",
          type: "warning",
        });
      } catch (e) {
        return;
      }
      try {
        const res = await this.$axios({
          url: "wrongbook/remove",
          method: "POST",
          data: { data: await this.$aesEncrypt({ id: b.id }) },
        });
        if (res.code === 200) {
          ElMessage.success("已删除");
          this.list = this.list.filter((x) => x.id !== b.id);
        }
      } catch (e) {
        ElMessage.error("删除失败");
      }
    },
  },
};
</script>
